import { createApi } from "@reduxjs/toolkit/query/react";
import { ThirdApi } from "../../api/baseApi";
import { removeClientToken, saveClientToken } from "../../helper/clientAuth";
import { loginSuccess, logout as logoutSuccess } from "../../store/slices/authSlice";

const extractClientToken = (payload) => {
  const tokenKeys = new Set([
    "token",
    "access_token",
    "accessToken",
    "bearer_token",
    "bearerToken",
    "auth_token",
    "authToken",
    "api_token",
    "apiToken",
    "jwt",
    "plainTextToken",
  ]);

  const findToken = (value) => {
    if (!value || typeof value !== "object") {
      return null;
    }

    for (const [key, nestedValue] of Object.entries(value)) {
      if (
        tokenKeys.has(key) &&
        typeof nestedValue === "string" &&
        nestedValue.length > 0
      ) {
        return nestedValue;
      }

      const nestedToken = findToken(nestedValue);

      if (nestedToken) {
        return nestedToken;
      }
    }

    return null;
  };

  return findToken(payload);
};

const axiosBaseQuery =
  ({ axiosInstance }) =>
    async ({ url, method, data, params }) => {
      try {
        const result = await axiosInstance({
          url,
          method,
          data,
          params,
        });

        const payload = result.data;
        const failed = payload?.success === false || payload?.status === false;

        if (failed) {
          return {
            error: {
              status: result.status,
              data: payload,
            },
          };
        }

        return { data: payload };
      } catch (axiosError) {
        return {
          error: {
            status: axiosError.response?.status,
            data: axiosError.response?.data || axiosError.message,
          },
        };
      }
    };

export const authApi = createApi({
  reducerPath: "authApi",
  baseQuery: axiosBaseQuery({ axiosInstance: ThirdApi }),
  endpoints: (builder) => ({
    login: builder.mutation({
      query: (credentials) => ({
        url: "/api/payway/login",
        method: "POST",
        data: credentials,
      }),
      async onQueryStarted(_credentials, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const token = extractClientToken(data);

          if (token) {
            saveClientToken(token);
            dispatch(
              loginSuccess({
                token,
              })
            );
          }
        } catch {
          // The component handles and displays login errors.
        }
      },
    }),
    register: builder.mutation({
      query: (payload) => ({
        url: "/api/payway/register",
        method: "POST",
        data: payload,
      }),
    }),
    verifyEmail: builder.mutation({
      query: (payload) => ({
        url: "/api/payway/verifyCode",
        method: "POST",
        data: payload,
      }),
      async onQueryStarted(payload, { queryFulfilled }) {
        if (payload?.purpose !== "email_change") return;
        try {
          const { data } = await queryFulfilled;
          const token = extractClientToken(data);
          if (token) saveClientToken(token);
        } catch {
          // The component handles verification errors.
        }
      },
    }),
    profile: builder.query({
      query: () => ({
        url: "/api/payway/profile",
        method: "GET",
      }),
    }),
    updateProfile: builder.mutation({
      query: (payload) => ({
        url: "/api/payway/profile",
        method: "PATCH",
        data: payload,
      }),
    }),
    changeEmail: builder.mutation({
      query: (payload) => ({
        url: "/api/payway/changeEmail",
        method: "POST",
        data: payload,
      }),
    }),
    forgotPassword: builder.mutation({
      query: (payload) => ({ url: "/api/payway/forgotPassword", method: "POST", data: payload }),
    }),
    resendCode: builder.mutation({
      query: (payload) => ({ url: "/api/payway/resendCode", method: "POST", data: payload }),
    }),
    resetPassword: builder.mutation({
      query: (payload) => ({ url: "/api/payway/resetPassword", method: "POST", data: payload }),
      async onQueryStarted(_payload, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          removeClientToken();
          dispatch(logoutSuccess());
          window.dispatchEvent(new Event("payways:auth-change"));
        } catch {
          // The form displays reset errors.
        }
      },
    }),
    logout: builder.mutation({
      query: () => ({
        url: "/api/payway/logout",
        method: "POST",
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } finally {
          removeClientToken();
          dispatch(logoutSuccess());
          // `useAuth` keeps a local copy of the client token. Notify it so
          // protected sections (including the partnership application) are
          // re-rendered immediately after logout.
          window.dispatchEvent(new Event("payways:auth-change"));
        }
      },
    }),
  }),
});

export const {
  useLoginMutation,
  useRegisterMutation,
  useVerifyEmailMutation,
  useProfileQuery,
  useUpdateProfileMutation,
  useChangeEmailMutation,
  useForgotPasswordMutation,
  useResendCodeMutation,
  useResetPasswordMutation,
  useLogoutMutation,
} = authApi;
